<template>
  <div class="honor-admin">
    <div class="page-head">
      <div>
        <h2>荣誉墙管理</h2>
        <p>管理 7 张竖版与 5 张横版荣誉图片。前台每次展示 4 张竖版和 3 张横版，并自动轮换。</p>
      </div>
      <div class="head-actions">
        <el-button type="primary" :loading="saving" @click="save">保存荣誉墙</el-button>
        <el-button :loading="loading" @click="load">重新读取</el-button>
      </div>
    </div>

    <el-alert
      title="图片会上传到本地 /uploads/ 目录；上传或修改后，请点击“保存荣誉墙”使前台生效。"
      type="info"
      show-icon
      :closable="false"
      class="admin-tip"
    />

    <el-card class="wall-preview-card" shadow="never">
      <div slot="header" class="preview-head">
        <div>
          <strong>前台墙面布局预览</strong>
          <span>当前预览素材库的第一轮展示</span>
        </div>
        <div class="rotation-control">
          <span>轮换间隔</span>
          <el-input-number
            v-model="honorWall.rotationSeconds"
            :min="5"
            :max="30"
            :step="1"
            size="mini"
          />
          <span>秒</span>
        </div>
      </div>
      <div class="wall-preview">
        <div class="preview-brand">KEJIAN <small>HONOR WALL</small></div>
        <div
          v-for="frame in previewFrames"
          :key="frame.position"
          class="preview-frame"
          :class="[`preview-frame--${frame.position}`, `preview-frame--${frame.orientation}`]"
        >
          <div v-if="frame.item.imageUrl" class="preview-frame__image" :style="imageStyle(frame.item)"></div>
          <div v-else class="preview-frame__empty">
            <strong>荣誉证书</strong>
            <small>{{ frame.orientation === "portrait" ? "竖版" : "横版" }}</small>
          </div>
        </div>
      </div>
    </el-card>

    <section class="honor-section">
      <div class="section-title">
        <div>
          <span class="section-index">01</span>
          <div>
            <h3>竖版荣誉图片</h3>
            <p>共 7 个固定素材位，前台每轮取其中 4 张。</p>
          </div>
        </div>
        <el-tag type="warning" effect="plain">7 PORTRAIT</el-tag>
      </div>
      <el-row :gutter="18">
        <el-col v-for="(item, index) in honorWall.portraits" :key="item.id" :xs="24" :sm="12" :md="8" :lg="6">
          <el-card class="editor-card editor-card--portrait" shadow="hover">
            <div class="editor-card__number">P{{ String(index + 1).padStart(2, "0") }}</div>
            <div class="editor-preview editor-preview--portrait">
              <div v-if="item.imageUrl" class="editor-preview__image" :style="imageStyle(item)"></div>
              <div v-else class="editor-preview__empty">
                <i class="el-icon-picture-outline"></i>
                <strong>竖版证书占位</strong>
                <span>建议图片比例约 3:4</span>
              </div>
            </div>
            <el-form label-position="top" size="small">
              <el-form-item label="荣誉名称">
                <el-input v-model="item.title" maxlength="40" show-word-limit />
              </el-form-item>
              <el-form-item label="图片地址">
                <el-input v-model="item.imageUrl" placeholder="上传图片后自动填写，也可粘贴地址" />
              </el-form-item>
            </el-form>
            <div class="editor-actions">
              <label class="upload-button" :class="{ 'is-loading': uploadingKey === item.id }">
                <input type="file" accept="image/*" :disabled="uploadingKey === item.id" @change="upload(item, $event)" />
                <i :class="uploadingKey === item.id ? 'el-icon-loading' : 'el-icon-upload2'"></i>
                {{ uploadingKey === item.id ? "上传中" : "选择图片" }}
              </label>
              <el-button size="mini" type="text" class="clear-button" :disabled="!item.imageUrl" @click="clearImage(item)">清除图片</el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section class="honor-section">
      <div class="section-title">
        <div>
          <span class="section-index">02</span>
          <div>
            <h3>横版荣誉图片</h3>
            <p>共 5 个固定素材位，前台每轮取其中 3 张。</p>
          </div>
        </div>
        <el-tag type="success" effect="plain">5 LANDSCAPE</el-tag>
      </div>
      <el-row :gutter="18">
        <el-col v-for="(item, index) in honorWall.landscapes" :key="item.id" :xs="24" :sm="12" :md="8">
          <el-card class="editor-card editor-card--landscape" shadow="hover">
            <div class="editor-card__number">L{{ String(index + 1).padStart(2, "0") }}</div>
            <div class="editor-preview editor-preview--landscape">
              <div v-if="item.imageUrl" class="editor-preview__image" :style="imageStyle(item)"></div>
              <div v-else class="editor-preview__empty">
                <i class="el-icon-picture-outline"></i>
                <strong>横版证书占位</strong>
                <span>建议图片比例约 4:3</span>
              </div>
            </div>
            <el-form label-position="top" size="small">
              <el-form-item label="荣誉名称">
                <el-input v-model="item.title" maxlength="40" show-word-limit />
              </el-form-item>
              <el-form-item label="图片地址">
                <el-input v-model="item.imageUrl" placeholder="上传图片后自动填写，也可粘贴地址" />
              </el-form-item>
            </el-form>
            <div class="editor-actions">
              <label class="upload-button" :class="{ 'is-loading': uploadingKey === item.id }">
                <input type="file" accept="image/*" :disabled="uploadingKey === item.id" @change="upload(item, $event)" />
                <i :class="uploadingKey === item.id ? 'el-icon-loading' : 'el-icon-upload2'"></i>
                {{ uploadingKey === item.id ? "上传中" : "选择图片" }}
              </label>
              <el-button size="mini" type="text" class="clear-button" :disabled="!item.imageUrl" @click="clearImage(item)">清除图片</el-button>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </section>
  </div>
</template>

<script>
import {
  getDefaultSiteConfig,
  loadEditableSiteConfig,
  saveSiteConfig,
  uploadSiteImage
} from "../../services/siteConfig";
const { normalizeHonorWall, getVisibleHonorItems } = require("../../services/honorWall");

export default {
  name: "HonorWallAdmin",
  data() {
    const config = getDefaultSiteConfig();
    return {
      loading: false,
      saving: false,
      uploadingKey: "",
      config,
      honorWall: normalizeHonorWall(config.honorWall)
    };
  },
  computed: {
    previewFrames() {
      const visible = getVisibleHonorItems(this.honorWall, 0);
      return [
        { position: "portrait-left", orientation: "portrait", item: visible.portraits[0] },
        { position: "landscape-left", orientation: "landscape", item: visible.landscapes[0] },
        { position: "portrait-mid-left", orientation: "portrait", item: visible.portraits[1] },
        { position: "landscape-main", orientation: "landscape", item: visible.landscapes[1] },
        { position: "portrait-mid-right", orientation: "portrait", item: visible.portraits[2] },
        { position: "landscape-right", orientation: "landscape", item: visible.landscapes[2] },
        { position: "portrait-right", orientation: "portrait", item: visible.portraits[3] }
      ];
    }
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      this.loading = true;
      loadEditableSiteConfig()
        .then(config => {
          this.config = config;
          this.honorWall = normalizeHonorWall(config.honorWall);
        })
        .catch(error => {
          this.$message.error(`读取荣誉墙失败：${error.message}`);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saving = true;
      this.config.honorWall = normalizeHonorWall(this.honorWall);
      saveSiteConfig(this.config)
        .then(config => {
          this.config = config;
          this.honorWall = normalizeHonorWall(config.honorWall);
          this.$message.success("荣誉墙配置已保存，刷新前台即可查看");
        })
        .catch(error => {
          this.$message.error(`保存失败：${error.message}`);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    upload(item, event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      this.uploadingKey = item.id;
      uploadSiteImage(file)
        .then(result => {
          this.$set(item, "imageUrl", result.url);
          this.$message.success(`${item.title || "荣誉图片"}上传成功`);
        })
        .catch(error => {
          this.$message.error(`上传失败：${error.message}`);
        })
        .finally(() => {
          this.uploadingKey = "";
        });
    },
    clearImage(item) {
      this.$set(item, "imageUrl", "");
    },
    imageStyle(item) {
      return item && item.imageUrl ? { backgroundImage: `url(${item.imageUrl})` } : {};
    }
  }
};
</script>

<style lang="scss" scoped>
.honor-admin {
  padding-bottom: 40px;
  text-align: left;
  color: #263746;
}

.page-head,
.preview-head,
.section-title,
.section-title > div,
.rotation-control,
.editor-actions {
  display: flex;
  align-items: center;
}

.page-head {
  justify-content: space-between;
  margin-bottom: 16px;

  h2 { margin: 0 0 8px; font-size: 26px; }
  p { margin: 0; color: #71808d; }
}

.head-actions > * { margin-left: 8px; }
.admin-tip { margin-bottom: 18px; }
.wall-preview-card { margin-bottom: 28px; border-color: #d8e0e6; }

.preview-head {
  justify-content: space-between;

  strong { display: block; font-size: 17px; }
  div > span { display: block; margin-top: 4px; color: #8a98a4; font-size: 12px; }
}

.rotation-control {
  gap: 8px;
  color: #657581;
  font-size: 13px;

  span { margin: 0 !important; color: #657581 !important; }
}

.wall-preview {
  position: relative;
  width: 100%;
  height: 360px;
  overflow: hidden;
  border-radius: 3px;
  background:
    radial-gradient(circle at center, rgba(63, 91, 107, 0.62), transparent 50%),
    linear-gradient(135deg, #142a36, #09151d 58%, #132b36);
  box-shadow: inset 0 0 60px rgba(0, 0, 0, 0.5);
}

.preview-brand {
  position: absolute;
  left: 50%;
  top: 4%;
  color: rgba(230, 207, 151, 0.74);
  font-family: Georgia, serif;
  font-size: 14px;
  letter-spacing: 5px;
  transform: translateX(-50%);

  small { margin-left: 8px; font-size: 7px; letter-spacing: 2px; opacity: 0.5; }
}

.preview-frame {
  position: absolute;
  padding: 5px;
  border: 1px solid #e4c87c;
  background: linear-gradient(135deg, #79511d, #e1c275, #684315, #d1aa54);
  box-shadow: 0 11px 20px rgba(0, 0, 0, 0.42), inset 0 0 0 2px rgba(74, 44, 10, 0.5);

  &--portrait-left { left: 3%; top: 20%; width: 12%; height: 58%; transform: rotate(-1deg); }
  &--landscape-left { left: 18%; top: 10%; width: 22%; height: 27%; }
  &--portrait-mid-left { left: 25%; top: 44%; width: 13%; height: 49%; }
  &--landscape-main { left: 40.5%; top: 28%; z-index: 2; width: 29%; height: 38%; }
  &--portrait-mid-right { left: 72%; top: 9%; width: 13%; height: 54%; }
  &--landscape-right { left: 63%; top: 69%; width: 22%; height: 24%; }
  &--portrait-right { left: 87%; top: 27%; width: 10%; height: 48%; }
}

.preview-frame__image,
.preview-frame__empty {
  width: 100%;
  height: 100%;
  border: 1px solid rgba(72, 48, 16, 0.7);
  background-color: #f3eddf;
}

.preview-frame__image {
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.preview-frame__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #8c6a2d;

  strong { font-size: 11px; letter-spacing: 2px; }
  small { margin-top: 5px; font-size: 8px; opacity: 0.65; }
}

.honor-section { margin-top: 30px; }

.section-title {
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8ed;

  h3 { margin: 0 0 5px; font-size: 20px; }
  p { margin: 0; color: #8896a1; font-size: 13px; }
}

.section-index {
  margin-right: 14px;
  color: #c59b45;
  font-family: Georgia, serif;
  font-size: 28px;
}

.editor-card {
  position: relative;
  margin-bottom: 18px;
  overflow: visible;
}

.editor-card__number {
  position: absolute;
  top: 11px;
  right: 12px;
  z-index: 3;
  padding: 3px 7px;
  border-radius: 2px;
  color: #d9ba72;
  background: rgba(15, 34, 45, 0.88);
  font-family: Georgia, serif;
  font-size: 11px;
  letter-spacing: 1px;
}

.editor-preview {
  width: 100%;
  margin-bottom: 14px;
  padding: 7px;
  border: 1px solid #ddc483;
  background: linear-gradient(135deg, #76501d, #e1c77e, #6d4818, #cda954);
  box-sizing: border-box;
  box-shadow: 0 8px 18px rgba(44, 52, 58, 0.15);

  &--portrait { height: 255px; }
  &--landscape { height: 210px; }
}

.editor-preview__image,
.editor-preview__empty {
  width: 100%;
  height: 100%;
  background-color: #f4efe3;
}

.editor-preview__image {
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
}

.editor-preview__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #9c895f;

  i { margin-bottom: 12px; font-size: 38px; color: #c6ad72; }
  strong { font-size: 15px; font-weight: 500; }
  span { margin-top: 7px; font-size: 12px; color: #a9a18e; }
}

.editor-actions {
  justify-content: space-between;
  margin-top: 2px;
}

.upload-button {
  display: inline-flex;
  align-items: center;
  padding: 7px 13px;
  border: 1px solid #409eff;
  border-radius: 4px;
  color: #409eff;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  transition: 0.2s;

  &:hover { color: #fff; background: #409eff; }
  &.is-loading { cursor: wait; opacity: 0.72; }
  i { margin-right: 5px; }
  input { display: none; }
}

.clear-button { color: #d56b5c; }

@media (max-width: 900px) {
  .page-head,
  .preview-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .head-actions,
  .rotation-control { margin-top: 12px; }
  .head-actions > :first-child { margin-left: 0; }
  .wall-preview { height: 260px; }
}
</style>



